import { IonContent, IonPage, IonSpinner, useIonToast } from "@ionic/react";
import { arrowBackOutline } from "ionicons/icons";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import Header from "src/components/Header";
import { Etiqueta, fmtFecha } from "src/components/SolicitudCapacitacionCampos";
import { SituacionChip } from "src/pages/SolicitudesGafcore";
import { fmtFechaHoraTexto, SolicitudTrasladoDetalle as Detalle, TrasladosAPI } from "src/services/traslados";

const VOLVER = "/gafcore/solicitudes?tab=mis";

const caja: React.CSSProperties = {
  background: "#f5f5f7",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 14,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const aviso = (fondo: string, borde: string): React.CSSProperties => ({
  background: fondo,
  borderLeft: `4px solid ${borde}`,
  borderRadius: 6,
  padding: "10px 12px",
  marginBottom: 16,
  fontSize: 14,
});

const Dato: React.FC<{ etiqueta: string; valor?: React.ReactNode }> = ({ etiqueta, valor }) => (
  <div style={{ marginBottom: 12 }}>
    <Etiqueta texto={etiqueta} />
    <div style={{ fontSize: 14, color: "#333" }}>{valor || "-"}</div>
  </div>
);

/**
 * Detalle de una Solicitud a GafCORE propia (/gafcore/solicitudes/:id). EMSYS
 * consulta GafCORE al abrirla: se ve el estado del momento, equipo por equipo.
 */
const SolicitudGafcoreDetalle: React.FC = () => {
  const [present] = useIonToast();
  const { id } = useParams<{ id: string }>();

  const [s, setS] = useState<Detalle | null>(null);
  const [cargando, setCargando] = useState(true);

  const avisar = useCallback(
    (message: string, color = "warning") => present({ message, duration: 3500, color }),
    [present]
  );

  useEffect(() => {
    const solicitudId = Number(id);
    if (!solicitudId) return;
    setCargando(true);
    TrasladosAPI.detalle(solicitudId)
      .then((r) => setS(r.data.data))
      .catch((err) => avisar(err?.response?.data?.error || "No se pudo obtener la solicitud", "danger"))
      .finally(() => setCargando(false));
  }, [id, avisar]);

  return (
    <IonPage>
      <Header titulo={`Solicitud a GafCORE N° ${id}`} botonIzq={{ icono: arrowBackOutline, url: VOLVER }} />
      <IonContent id="gafcore-solicitud-detalle">
        <div style={{ padding: 16, maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
          {cargando ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner />
            </div>
          ) : !s ? (
            <p style={{ color: "#777" }}>La solicitud no existe.</p>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <SituacionChip etiqueta={s.situacion_label} color={s.situacion_color} />
                <strong style={{ fontSize: 14 }}>{s.tipo_label}</strong>
                {s.gaf_codigo && <span style={{ fontSize: 13, color: "#777" }}>{s.gaf_codigo}</span>}
              </div>

              {(s.envio_estado === "ERROR" || s.envio_estado === "PENDIENTE") && (
                <div style={aviso("#fff8e6", "#f0a202")}>
                  La solicitud quedó registrada pero todavía no llegó a GafCORE. Se reenvía sola.
                </div>
              )}
              {s.envio_estado === "RECHAZADA" && (
                <div style={aviso("#fdecea", "#c0392b")}>GafCORE rechazó la solicitud: {s.envio_error}</div>
              )}
              {s.sin_conexion && s.envio_estado === "ENVIADA" && (
                <div style={aviso("#fff8e6", "#f0a202")}>
                  GafCORE no respondió: se muestra el último estado conocido.
                </div>
              )}
              {s.motivo_cierre && (
                <div style={aviso("#f4f4f4", "#95a5a6")}>
                  <strong>Motivo de cierre</strong>
                  <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{s.motivo_cierre}</div>
                </div>
              )}

              <Dato etiqueta="Desde" valor={s.origen} />
              {s.origen_coincide === false && s.equipos_solicitados.length > 0 && (
                <small style={{ color: "#b58100", display: "block", marginTop: -8, marginBottom: 12 }}>
                  Según GafCORE los equipos estaban en {s.origen_segun_gafcore || "otro lugar o sin ubicación"}
                </small>
              )}
              <Dato etiqueta="Hasta" valor={s.destino} />
              <Dato
                etiqueta="Para el día"
                valor={s.fecha_requerida ? `${fmtFecha(s.fecha_requerida)}${s.horario ? ` · ${s.horario}` : ""}` : "Sin fecha"}
              />
              <Dato etiqueta="Técnico asignado" valor={s.asignado_a || "Todavía sin asignar"} />
              <div style={{ marginBottom: 16 }}>
                <Etiqueta texto="Detalle" />
                <div style={caja}>{s.detalle}</div>
              </div>

              {(s.equipos.length > 0 || s.equipos_solicitados.length > 0) && (
                <div style={{ marginBottom: 16 }}>
                  <Etiqueta
                    texto={`Equipos${s.equipos_pendientes != null ? ` (${s.equipos_pendientes} sin mover)` : ""}`}
                  />
                  {s.equipos.length > 0
                    ? s.equipos.map((e, i) => (
                        <div
                          key={`${e.codigo_g}-${e.traslado || i}`}
                          style={{
                            background: "#fff",
                            borderRadius: 8,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                            padding: "8px 10px",
                            marginBottom: 6,
                            borderLeft: `4px solid ${e.situacion_color}`,
                            fontSize: 13,
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                            <strong>
                              {e.codigo_g}
                              {e.cod_fracttal ? ` / ${e.cod_fracttal}` : ""}
                            </strong>
                            <SituacionChip etiqueta={e.situacion_label} color={e.situacion_color} />
                          </div>
                          <div style={{ color: "#444" }}>{e.nombre}</div>
                          {(e.salida_en || e.llegada_en) && (
                            <div style={{ color: "#777", fontSize: 12 }}>
                              {e.traslado ? `${e.traslado} · ` : ""}
                              Salió {fmtFechaHoraTexto(e.salida_en)} · Llegó {fmtFechaHoraTexto(e.llegada_en)}
                            </div>
                          )}
                          {e.motivo && <div style={{ color: "#b58100", fontSize: 12 }}>{e.motivo}</div>}
                        </div>
                      ))
                    : s.equipos_solicitados.map((e) => (
                        <div key={e.codigo} style={{ ...caja, marginBottom: 6 }}>
                          {e.codigo_g || e.codigo} · {e.nombre || "-"}
                        </div>
                      ))}
                </div>
              )}

              {s.hitos.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Etiqueta texto="Seguimiento" />
                  {s.hitos.map((h) => (
                    <div key={h.etiqueta} style={{ fontSize: 13, color: "#444", marginBottom: 4 }}>
                      <strong>{h.etiqueta}:</strong> {fmtFechaHoraTexto(h.fecha)}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SolicitudGafcoreDetalle;
